// @ts-check
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './LevelCloseUp.css';

/**
 * LevelCloseUp - tap any level to see its beast up close.
 *
 * Renders through a portal so it sits above the level bottom sheet, which is
 * itself portalled to body.
 *
 * @param {Object} props
 * @param {Object} props.level - Level object from levelsConfig
 * @param {number} props.points - Student's current points, for the locked hint
 * @param {() => void} props.onClose
 */
export default function LevelCloseUp({ level, points, onClose }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!level) return null;

  const isUnlocked = points >= level.minPoints;
  const variant = level.variant;

  return createPortal(
    <div
      className="level-closeup-overlay"
      onClick={onClose}
      data-testid="level-closeup-overlay"
    >
      <div
        className={`level-closeup-card${variant ? ` variant-${variant}` : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={level.name}
      >
        <button className="level-closeup-close" onClick={onClose} aria-label="关闭">
          <X size={20} />
        </button>

        <div
          className={`level-closeup-icon${variant ? ` ${variant}` : ''}${isUnlocked ? '' : ' locked'}`}
          style={!variant && isUnlocked ? {
            background: `linear-gradient(145deg, ${level.colorLight} 0%, ${level.color} 100%)`,
            boxShadow: `0 6px 0 ${level.colorDark}, 0 12px 32px ${level.color}55`,
          } : undefined}
        >
          <span>{level.icon}</span>
        </div>

        <div className="level-closeup-info">
          <span className="level-closeup-name">{level.name}</span>
          <span className="level-closeup-name-en">{level.nameEn}</span>
          <span className="level-closeup-lv">Lv.{level.level} · {level.minPoints}+ 积分</span>
        </div>

        {isUnlocked ? (
          <span className="level-closeup-status unlocked">已解锁 ✓</span>
        ) : (
          <span className="level-closeup-status locked">
            还需 {level.minPoints - points} 积分解锁
          </span>
        )}
      </div>
    </div>,
    document.body,
  );
}
